import { useEffect, useState } from 'react';
import { useProjectStore, useUIStore } from '@/stores';
import { Modal, Button, Input } from '@/components/ui';
import { Save, RotateCcw, Trash2, Clock, Check, AlertCircle } from 'lucide-react';
import type { Version } from '@/types';

export function VersionModal() {
  const { isVersionModalOpen, closeVersionModal } = useUIStore();
  const { currentProject, versions, tasks, loadVersions, createVersion, deleteVersion, setTasks, setCurrentVersion, updateVehicleNodes } = useProjectStore();
  const [isLoading, setIsLoading] = useState(false);
  const [versionName, setVersionName] = useState('');
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<Version | null>(null);
  const [showLoadConfirm, setShowLoadConfirm] = useState(false);

  // 生成默认版本号
  useEffect(() => {
    if (isVersionModalOpen && currentProject) {
      const date = new Date();
      const dateStr = date.toISOString().split('T')[0].replace(/-/g, '');
      const timeStr = date.toTimeString().slice(0, 5).replace(':', '');
      setVersionName(`V${dateStr}_${timeStr}`);
    }
  }, [isVersionModalOpen, currentProject]);

  useEffect(() => {
    if (currentProject && isVersionModalOpen) {
      loadVersions(currentProject.id);
    }
  }, [currentProject, isVersionModalOpen]);

  const handleSaveVersion = async () => {
    if (!currentProject || !versionName.trim()) return;

    setIsLoading(true);
    try {
      const newVersion = await createVersion({
        project_id: currentProject.id,
        version: versionName.trim(),
        task_list: tasks,
        vehicle_nodes: currentProject.nodes,
      });

      // 设置当前版本
      setCurrentVersion(newVersion);
      setVersionName('');
      setShowSaveConfirm(false);
    } catch (error) {
      console.error('Save version error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoadVersionClick = (version: Version) => {
    setSelectedVersion(version);
    setShowLoadConfirm(true);
  };

  const handleLoadVersion = () => {
    if (!selectedVersion) return;

    // 显示 loading 遮罩
    useProjectStore.setState({ isProjectSwitching: true });

    // 使用正确的字段名：task_list
    if (selectedVersion.task_list) {
      setTasks(selectedVersion.task_list);
    }

    // 更新 vehicle nodes
    if (selectedVersion.vehicle_nodes) {
      updateVehicleNodes(selectedVersion.vehicle_nodes);
    }

    // 设置当前版本
    setCurrentVersion(selectedVersion);

    setShowLoadConfirm(false);
    setSelectedVersion(null);
    closeVersionModal();
  };

  const handleDeleteVersion = async (version: Version) => {
    if (!currentProject) return;
    try {
      await deleteVersion(currentProject.id, version.version, version.id);
    } catch (error) {
      console.error('Delete version error:', error);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <>
      <Modal
        isOpen={isVersionModalOpen}
        onClose={closeVersionModal}
        title="版本历史"
        size="lg"
      >
        <div className="space-y-4">
          {/* Save New Version */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-medium text-gray-700 mb-2">保存当前版本</h4>
            <div className="flex space-x-2">
              <Input
                value={versionName}
                onChange={(e) => setVersionName(e.target.value)}
                placeholder="输入版本号（如：V20240228_1430）"
                className="flex-1"
              />
              <Button
                onClick={() => setShowSaveConfirm(true)}
                isLoading={isLoading}
                leftIcon={<Save className="w-4 h-4" />}
              >
                保存
              </Button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              建议格式：VYYYYMMDD_HHMM 或自定义版本号
            </p>
          </div>

          {/* Current Version Info */}
          {versions.length > 0 && (
            <div className="bg-blue-50 p-3 rounded-lg">
              <div className="flex items-center text-blue-700">
                <Check className="w-4 h-4 mr-2" />
                <span className="text-sm">
                  当前共有 {versions.length} 个历史版本
                </span>
              </div>
            </div>
          )}

          {/* Version List */}
          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            <h4 className="font-medium text-gray-700">历史版本</h4>
            
            {versions.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                暂无保存的版本
              </div>
            ) : (
              versions.map((version, index) => (
                <div
                  key={version.id}
                  className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 text-sm font-medium">
                      {versions.length - index}
                    </div>
                    <div>
                      <div className="font-medium text-gray-800">{version.version}</div>
                      <div className="text-xs text-gray-500">
                        <Clock className="w-3 h-3 inline mr-1" />
                        {formatDate(version.created_at)}
                      </div>
                      {version.task_list && (
                        <div className="text-xs text-gray-400 mt-1">
                          包含 {version.task_list.length} 个任务
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      leftIcon={<RotateCcw className="w-4 h-4" />}
                      onClick={() => handleLoadVersionClick(version)}
                    >
                      恢复
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      leftIcon={<Trash2 className="w-4 h-4 text-red-500" />}
                      onClick={() => handleDeleteVersion(version)}
                    >
                      删除
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </Modal>

      {/* Save Confirm Modal */}
      {showSaveConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[85vh] overflow-y-auto p-6">
            <div className="flex items-center text-blue-600 mb-4">
              <Save className="w-6 h-6 mr-2" />
              <h3 className="text-lg font-bold">确认保存版本</h3>
            </div>
            <p className="text-gray-600 mb-2">
              版本号：<span className="font-medium text-gray-800">{versionName}</span>
            </p>
            <p className="text-gray-600 mb-6">
              将保存当前 {tasks.length} 个任务和节点信息。是否继续？
            </p>
            <div className="flex justify-end space-x-3">
              <Button variant="ghost" onClick={() => setShowSaveConfirm(false)}>
                取消
              </Button>
              <Button onClick={handleSaveVersion} isLoading={isLoading}>
                确认保存
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Load Confirm Modal */}
      {showLoadConfirm && selectedVersion && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[85vh] overflow-y-auto p-6">
            <div className="flex items-center text-orange-600 mb-4">
              <AlertCircle className="w-6 h-6 mr-2" />
              <h3 className="text-lg font-bold">确认恢复版本</h3>
            </div>
            <p className="text-gray-600 mb-2">
              版本号：<span className="font-medium text-gray-800">{selectedVersion.version}</span>
            </p>
            <p className="text-gray-600 mb-6">
              恢复此版本将覆盖当前的任务数据。此操作不可撤销，是否继续？
            </p>
            <div className="flex justify-end space-x-3">
              <Button variant="ghost" onClick={() => setShowLoadConfirm(false)}>
                取消
              </Button>
              <Button variant="primary" onClick={handleLoadVersion}>
                确认恢复
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
